import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  QuestionMark,
  AudioFile,
  TextFields,
  RadioButtonChecked,
} from '@mui/icons-material';
import {
  CUSTOM_QUESTION_TYPE_VALUE,
  KNOWN_QUESTION_TYPES,
  getQuestionTypeSelection,
  isValidCustomQuestionTypeSlug,
  resolveQuestionType,
} from '../constants/questionTypes';

interface Section {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  instructions?: any;
  language_id: number;
}

interface Subsection {
  id: string;
  section_id: string;
  name: string;
  description?: string;
  is_active: boolean;
  instructions?: any;
  language_id: number;
}

interface Question {
  id: string;
  text: string;
  question_type: string;
  section_id?: string;
  sub_section_id?: string;
  section_name: string;
  subsection_name: string;
  difficulty_level: string;
  max_score: number;
  score: number;
  audio_url?: string;
  paragraph_text?: string;
  options?: string[];
  correct_answer?: string;
  created_at: string;
  // New properties for enhanced content structure
  parent_question_id?: string;
  parent_question_text?: string;
  // Section and Subsection objects (new)
  section?: Section;
  subsection?: Subsection;
  content?: {
    question_type?: string;
    title?: string;
    difficulty_level?: string;
    audio_url?: string;
    paragraph?: string;
    instructions?: string;
    duration_minutes?: number;
    total_child_questions?: number;
    question_number?: number;
    depends_on_parent?: boolean;
    options?: string[];
    correct_answer?: string;
    time_limit_seconds?: number;
    evaluation_criteria?: string[];
  };
}

const emptyFormData = {
  text: '',
  question_type: '',
  custom_question_type: '',
  section_id: '',
  subsection_id: '',
  parent_question_id: '',
  difficulty_level: '',
  max_score: 10,
  duration_minutes: 5,
  audio_url: '',
  paragraph: '',
  instructions: '',
  total_child_questions: 1,
  question_number: 1,
  depends_on_parent: false,
  options: ['', '', '', ''] as string[],
  correct_answer: '',
};

const ViewQuestions: React.FC = () => {
  const navigate = useNavigate();
  
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [formData, setFormData] = useState(emptyFormData);

  const effectiveQuestionType = resolveQuestionType(
    formData.question_type,
    formData.custom_question_type
  );
  const isCustomTypeSelected = formData.question_type === CUSTOM_QUESTION_TYPE_VALUE;
  const customTypeValid =
    !isCustomTypeSelected || isValidCustomQuestionTypeSlug(formData.custom_question_type);
  
  // Sections and subsections for filters / edit form
  const [sections, setSections] = useState<any[]>([]);
  const [subsections, setSubsections] = useState<any[]>([]);
  const [parentQuestions, setParentQuestions] = useState<any[]>([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const questionsPerPage = 10;
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');

  useEffect(() => {
    loadQuestions();
    loadSections();
  }, [currentPage, searchTerm, sectionFilter, typeFilter, difficultyFilter]);
  
  const loadSections = async () => {
    try {
      const sectionsResponse = await fetch(API_ENDPOINTS.SECTIONS);
      const sectionsData = await sectionsResponse.json();
      
      if (sectionsData.status === 'success') {
        setSections(sectionsData.data);
      }

      const subsectionsResponse = await fetch(API_ENDPOINTS.SUBSECTIONS);
      const subsectionsData = await subsectionsResponse.json();
      
      if (subsectionsData.status === 'success') {
        setSubsections(subsectionsData.data);
      }

      const parentQuestionsResponse = await fetch(API_ENDPOINTS.PARENT_QUESTIONS);
      const parentQuestionsData = await parentQuestionsResponse.json();

      if (parentQuestionsData.status === 'success') {
        setParentQuestions(parentQuestionsData.data);
      }
    } catch (err) {
      console.error('Failed to load sections:', err);
    }
  };

  const loadQuestions = async () => {
    try {
      setLoading(true);
      
      const params = {
        skip: (currentPage - 1) * questionsPerPage,
        limit: questionsPerPage,
        search: searchTerm || undefined,
        section_id: sectionFilter || undefined,
        question_type: typeFilter || undefined,
        difficulty_level: difficultyFilter || undefined,
      };
      
      const response = await fetch(API_ENDPOINTS.QUESTIONS_WITH_FILTERS(params));
      const result = await response.json();
      
      if (result.status === 'success') {
        setQuestions(result.data);
        setTotalQuestions(result.pagination.total);
        setTotalPages(Math.ceil(result.pagination.total / questionsPerPage) || 1);
      } else {
        setError(result.message || 'Failed to load questions');
      }
      
    } catch (err) {
      setError('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const buildQuestionPayload = () => {
    const content = {
      question_type: effectiveQuestionType,
      difficulty_level: formData.difficulty_level,
      audio_url: formData.audio_url || null,
      paragraph: formData.paragraph || null,
      instructions: formData.instructions || null,
      duration_minutes: formData.duration_minutes || null,
      total_child_questions: formData.total_child_questions || null,
      question_number: formData.question_number || null,
      depends_on_parent: formData.depends_on_parent || false,
      options: effectiveQuestionType === 'multiple_choice' ? formData.options : null,
      correct_answer: effectiveQuestionType === 'multiple_choice' ? formData.correct_answer : null,
    };

    const cleanContent = Object.fromEntries(
      Object.entries(content).filter(([_, value]) => value !== null && value !== '')
    );

    return {
      text: formData.text,
      section_id: formData.section_id,
      subsection_id: formData.subsection_id,
      parent_question_id: formData.parent_question_id || null,
      max_score: formData.max_score,
      duration_minutes: formData.duration_minutes,
      content: cleanContent,
      question_type: effectiveQuestionType,
      difficulty_level: formData.difficulty_level,
      audio_url: formData.audio_url || null,
      paragraph: formData.paragraph || null,
      instructions: formData.instructions || null,
      total_child_questions: formData.total_child_questions || null,
      question_number: formData.question_number || null,
      depends_on_parent: formData.depends_on_parent || false,
      options: effectiveQuestionType === 'multiple_choice' ? formData.options : null,
      correct_answer: effectiveQuestionType === 'multiple_choice' ? formData.correct_answer : null,
    };
  };

  const openEditDialog = (question: Question) => {
    const content = question.content || {};
    const options = content.options || question.options || ['', '', '', ''];
    const paddedOptions = [...options];
    while (paddedOptions.length < 4) {
      paddedOptions.push('');
    }

    setEditingQuestion(question);
    const existingType = content.question_type || question.question_type || '';
    const typeSelection = getQuestionTypeSelection(existingType);
    setFormData({
      text: question.text || '',
      question_type: typeSelection,
      custom_question_type:
        typeSelection === CUSTOM_QUESTION_TYPE_VALUE ? existingType : '',
      section_id: question.section_id || question.section?.id || '',
      subsection_id: question.sub_section_id || question.subsection?.id || '',
      parent_question_id: question.parent_question_id || '',
      difficulty_level: content.difficulty_level || question.difficulty_level || '',
      max_score: question.score || question.max_score || 10,
      duration_minutes: content.duration_minutes || 5,
      audio_url: content.audio_url || question.audio_url || '',
      paragraph: content.paragraph || question.paragraph_text || '',
      instructions: content.instructions || '',
      total_child_questions: content.total_child_questions || 1,
      question_number: content.question_number || 1,
      depends_on_parent: content.depends_on_parent || false,
      options: paddedOptions.slice(0, 4),
      correct_answer: content.correct_answer || question.correct_answer || '',
    });
    setEditDialogOpen(true);
  };

  const handleUpdateQuestion = async () => {
    if (!editingQuestion) return;

    try {
      if (!effectiveQuestionType) {
        setError('Please select or enter a question type');
        return;
      }
      if (isCustomTypeSelected && !customTypeValid) {
        setError('Custom type must be snake_case (e.g. opinion_reasoning)');
        return;
      }

      setSaving(true);
      setError('');

      const response = await fetch(API_ENDPOINTS.UPDATE_QUESTION(editingQuestion.id), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildQuestionPayload()),
      });

      const result = await response.json();

      if (result.status === 'success') {
        setSuccess('Question updated successfully!');
        setEditDialogOpen(false);
        setEditingQuestion(null);
        setFormData(emptyFormData);
        setViewDialogOpen(false);
        loadQuestions();
        loadSections();
      } else {
        setError(result.message || result.detail || 'Failed to update question');
      }
    } catch (err) {
      console.error('Error updating question:', err);
      setError('Failed to update question');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (question: Question) => {
    const isParent = !question.parent_question_id;
    const confirmMessage = isParent
      ? 'Are you sure you want to delete this question? If it has child questions, they will also be soft-deleted.'
      : 'Are you sure you want to delete this question?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await fetch(API_ENDPOINTS.DELETE_QUESTION(question.id), {
        method: 'DELETE',
      });
      const result = await response.json();

      if (result.status === 'success') {
        const childrenCount = result.data?.deleted_children_count || 0;
        setSuccess(
          childrenCount > 0
            ? `Question deleted successfully (${childrenCount} child question(s) also deleted).`
            : 'Question deleted successfully!'
        );
        setViewDialogOpen(false);
        setSelectedQuestion(null);
        loadQuestions();
        loadSections();
      } else {
        setError(result.message || result.detail || 'Failed to delete question');
      }
    } catch (err) {
      console.error('Error deleting question:', err);
      setError('Failed to delete question');
    } finally {
      setLoading(false);
    }
  };

  const getQuestionTypeIcon = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return <RadioButtonChecked color="primary" />;
      case 'text_input':
        return <TextFields color="success" />;
      case 'audio_comprehension':
        return <AudioFile color="warning" />;
      case 'reading_comprehension':
        return <TextFields color="info" />;
      case 'listening_comprehension_set':
        return <AudioFile color="secondary" />;
      case 'reading_comprehension_set':
        return <TextFields color="secondary" />;
      case 'future_planning':
      case 'problem_solving':
      case 'opinion_reasoning':
      case 'personal_background':
        return <TextFields color="primary" />;
      case 'reading_fluency_individual':
        return <TextFields color="success" />;
      default:
        return <QuestionMark />;
    }
  };

  const getQuestionTypeColor = (type: string) => {
    switch (type) {
      case 'multiple_choice':
        return 'primary';
      case 'text_input':
        return 'success';
      case 'audio_comprehension':
        return 'warning';
      case 'reading_comprehension':
        return 'info';
      case 'listening_comprehension_set':
        return 'secondary';
      case 'reading_comprehension_set':
        return 'secondary';
      case 'future_planning':
      case 'problem_solving':
      case 'opinion_reasoning':
      case 'personal_background':
        return 'primary';
      case 'reading_fluency_individual':
        return 'success';
      default:
        return 'default';
    }
  };

  const getDifficultyColor = (level: string) => {
    switch (level) {
      case 'beginner':
        return 'success';
      case 'intermediate':
        return 'warning';
      case 'advanced':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box sx={{ p: 4, minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/questions')}
          variant="outlined"
        >
          Back to Questions
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h3" fontWeight={800} className="gradient-text" gutterBottom>
            📋 All Questions ({totalQuestions})
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Browse and manage all questions with pagination and filters
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: '12px' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon /> Filters & Search
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250 }}
            />
            
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Section</InputLabel>
              <Select
                value={sectionFilter}
                onChange={(e) => setSectionFilter(e.target.value)}
              >
                <MenuItem value="">All Sections</MenuItem>
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="multiple_choice">Multiple Choice</MenuItem>
                <MenuItem value="text_input">Text Input</MenuItem>
                <MenuItem value="audio_comprehension">Audio</MenuItem>
                <MenuItem value="reading_comprehension">Reading</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <MenuItem value="">All Levels</MenuItem>
                <MenuItem value="beginner">Beginner</MenuItem>
                <MenuItem value="intermediate">Intermediate</MenuItem>
                <MenuItem value="advanced">Advanced</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => {
                setSearchTerm('');
                setSectionFilter('');
                setTypeFilter('');
                setDifficultyFilter('');
                setCurrentPage(1);
              }}
            >
              Clear Filters
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Questions Table */}
      <Card sx={{ borderRadius: '16px' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              Questions (Page {currentPage} of {totalPages})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Showing {questions.length} of {totalQuestions} questions
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Loading questions...
              </Typography>
            </Box>
          ) : questions.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
                No questions found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or search terms
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Question</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Section</strong></TableCell>
                      <TableCell><strong>Difficulty</strong></TableCell>
                      <TableCell><strong>Score</strong></TableCell>
                      <TableCell><strong>Created</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {questions.map((question) => (
                      <TableRow key={question.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 300 }}>
                            {question.text.length > 60 
                              ? `${question.text.substring(0, 60)}...` 
                              : question.text
                            }
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {question.subsection_name || 'No subsection'}
                          </Typography>
                          {question.parent_question_id && (
                            <Typography variant="caption" color="primary" sx={{ display: 'block' }}>
                              🔗 Child Question
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getQuestionTypeIcon(question.content?.question_type || question.question_type || 'unknown')}
                            label={question.content?.question_type ? question.content.question_type.replace('_', ' ') : (question.question_type ? question.question_type.replace('_', ' ') : 'Unknown')}
                            color={getQuestionTypeColor(question.content?.question_type || question.question_type || 'unknown') as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {question.section?.name || question.section_name || 'Unknown Section'}
                          </Typography>
                          {question.subsection?.name && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {question.subsection.name}
                            </Typography>
                          )}
                          {question.content?.title && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {question.content.title}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={question.content?.difficulty_level || question.difficulty_level || 'Intermediate'}
                            color={getDifficultyColor(question.content?.difficulty_level || question.difficulty_level || 'intermediate') as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {question.score || question.max_score} pts
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(question.created_at).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedQuestion(question);
                                setViewDialogOpen(true);
                              }}
                              title="View Details"
                            >
                              <ViewIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="primary"
                              title="Edit Question"
                              onClick={() => openEditDialog(question)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDelete(question)}
                              title="Delete Question"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                />
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* View Question Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            👁️ Question Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedQuestion && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedQuestion.text}
              </Typography>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, mb: 3 }}>
                <Chip
                  icon={getQuestionTypeIcon(selectedQuestion.question_type || 'unknown')}
                  label={selectedQuestion.question_type ? selectedQuestion.question_type.replace('_', ' ') : 'Unknown'}
                  color={getQuestionTypeColor(selectedQuestion.question_type || 'unknown') as any}
                />
                <Chip
                  label={selectedQuestion.content?.difficulty_level || selectedQuestion.difficulty_level || 'Intermediate'}
                  color={getDifficultyColor(selectedQuestion.content?.difficulty_level || selectedQuestion.difficulty_level || 'intermediate') as any}
                />
                <Chip
                  label={`${selectedQuestion.score || selectedQuestion.max_score} points`}
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Section:</strong> {selectedQuestion.section?.name || selectedQuestion.section_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Subsection:</strong> {selectedQuestion.subsection?.name || selectedQuestion.subsection_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Created:</strong> {new Date(selectedQuestion.created_at).toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Question ID:</strong> {selectedQuestion.id}
                </Typography>
                {selectedQuestion.parent_question_id && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Parent Question:</strong> {selectedQuestion.parent_question_text || 'Linked'}
                  </Typography>
                )}
                {selectedQuestion.content?.duration_minutes && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Duration:</strong> {selectedQuestion.content.duration_minutes} minutes
                  </Typography>
                )}
              </Box>

              {/* Display Section Details if available */}
              {selectedQuestion.section && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="primary.main">
                    📂 Section Details:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedQuestion.section.name}
                  </Typography>
                  {selectedQuestion.section.description && (
                    <Typography variant="body2">
                      <strong>Description:</strong> {selectedQuestion.section.description}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Display Subsection Details if available */}
              {selectedQuestion.subsection && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="secondary.main">
                    📑 Subsection Details:
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedQuestion.subsection.name}
                  </Typography>
                  {selectedQuestion.subsection.description && (
                    <Typography variant="body2">
                      <strong>Description:</strong> {selectedQuestion.subsection.description}
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Instructions */}
              {selectedQuestion.content?.instructions && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="info.main">
                    📋 Instructions:
                  </Typography>
                  <Typography variant="body2">
                    {selectedQuestion.content.instructions}
                  </Typography>
                </Box>
              )}
              
              {/* Audio URL */}
              {selectedQuestion.content?.audio_url && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="warning.main">
                    🔊 Audio Content:
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {selectedQuestion.content.audio_url}
                  </Typography>
                </Box>
              )}
              
              {/* Paragraph Content */}
              {selectedQuestion.content?.paragraph && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="success.main">
                    📖 Content/Paragraph:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedQuestion.content.paragraph}
                  </Typography>
                </Box>
              )}
              
              {/* Child Question Info */}
              {selectedQuestion.content?.total_child_questions && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'secondary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="secondary.main">
                    👥 Comprehension Set Info:
                  </Typography>
                  <Typography variant="body2">
                    Total Child Questions: {selectedQuestion.content.total_child_questions}
                  </Typography>
                  {selectedQuestion.content.question_number && (
                    <Typography variant="body2">
                      Question Number: {selectedQuestion.content.question_number}
                    </Typography>
                  )}
                  {selectedQuestion.content.depends_on_parent && (
                    <Typography variant="body2">
                      Depends on Parent: Yes
                    </Typography>
                  )}
                </Box>
              )}
              
              {/* Time Limit */}
              {selectedQuestion.content?.time_limit_seconds && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="warning.main">
                    ⏱️ Time Limit:
                  </Typography>
                  <Typography variant="body2">
                    {selectedQuestion.content.time_limit_seconds} seconds
                  </Typography>
                </Box>
              )}
              
              {/* Evaluation Criteria */}
              {selectedQuestion.content?.evaluation_criteria && (
                <Box sx={{ mb: 3, p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom color="info.main">
                    📊 Evaluation Criteria:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedQuestion.content.evaluation_criteria.map((criteria, index) => (
                      <Chip
                        key={index}
                        label={criteria.replace('_', ' ')}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {selectedQuestion.options && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Answer Options:
                  </Typography>
                  {selectedQuestion.options.map((option, index) => (
                    <Typography key={index} variant="body2" sx={{ ml: 2, mb: 1 }}>
                      {String.fromCharCode(65 + index)}. {option}
                    </Typography>
                  ))}
                  {selectedQuestion.correct_answer && (
                    <Typography variant="body2" color="success.main" sx={{ mt: 2, ml: 2, fontWeight: 600 }}>
                      ✓ Correct Answer: {selectedQuestion.correct_answer}
                    </Typography>
                  )}
                </Box>
              )}

              {selectedQuestion.audio_url && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Audio URL:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2, fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    {selectedQuestion.audio_url}
                  </Typography>
                </Box>
              )}

              {selectedQuestion.paragraph_text && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Reading Passage:
                  </Typography>
                  <Typography variant="body2" sx={{ ml: 2, bgcolor: 'grey.50', p: 2, borderRadius: 1, fontStyle: 'italic' }}>
                    {selectedQuestion.paragraph_text}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => {
              if (selectedQuestion) {
                setViewDialogOpen(false);
                openEditDialog(selectedQuestion);
              }
            }}
          >
            Edit Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Question Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (!saving) {
            setEditDialogOpen(false);
            setEditingQuestion(null);
            setFormData(emptyFormData);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Edit Question
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="Question Text"
              multiline
              rows={3}
              value={formData.text}
              onChange={(e) => setFormData({ ...formData, text: e.target.value })}
              placeholder="Enter the question text..."
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Question Type</InputLabel>
                <Select
                  value={formData.question_type}
                  label="Question Type"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      question_type: e.target.value,
                      custom_question_type:
                        e.target.value === CUSTOM_QUESTION_TYPE_VALUE
                          ? formData.custom_question_type
                          : '',
                    })
                  }
                >
                  {KNOWN_QUESTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                  <MenuItem value={CUSTOM_QUESTION_TYPE_VALUE}>Custom / Other</MenuItem>
                </Select>
              </FormControl>

              {isCustomTypeSelected && (
                <TextField
                  label="Custom Type Slug"
                  value={formData.custom_question_type}
                  onChange={(e) =>
                    setFormData({ ...formData, custom_question_type: e.target.value })
                  }
                  placeholder="e.g. opinion_reasoning"
                  helperText={
                    formData.custom_question_type && !customTypeValid
                      ? 'Use snake_case: lowercase letters, numbers, underscores'
                      : 'Must match assessment question_selection key'
                  }
                  error={Boolean(formData.custom_question_type) && !customTypeValid}
                  sx={{ minWidth: 240 }}
                />
              )}

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Section</InputLabel>
                <Select
                  value={formData.section_id}
                  label="Section"
                  onChange={(e) => setFormData({ ...formData, section_id: e.target.value, subsection_id: '' })}
                >
                  {sections.map((section) => (
                    <MenuItem key={section.id} value={section.id}>
                      {section.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Subsection</InputLabel>
                <Select
                  value={formData.subsection_id}
                  label="Subsection"
                  onChange={(e) => setFormData({ ...formData, subsection_id: e.target.value })}
                  disabled={!formData.section_id}
                >
                  {subsections
                    .filter((sub) => sub.section_id === formData.section_id)
                    .map((subsection) => (
                      <MenuItem key={subsection.id} value={subsection.id}>
                        {subsection.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Parent Question (Optional)</InputLabel>
                <Select
                  value={formData.parent_question_id}
                  label="Parent Question (Optional)"
                  onChange={(e) => setFormData({ ...formData, parent_question_id: e.target.value })}
                  disabled={!formData.section_id}
                >
                  <MenuItem value="">None (Standalone Question)</MenuItem>
                  {parentQuestions
                    .filter(
                      (parent) =>
                        parent.id !== editingQuestion?.id &&
                        parent.section_id === formData.section_id &&
                        (!formData.subsection_id || parent.sub_section_id === formData.subsection_id)
                    )
                    .map((parentQuestion) => (
                      <MenuItem key={parentQuestion.id} value={parentQuestion.id}>
                        {parentQuestion.text.length > 50
                          ? `${parentQuestion.text.substring(0, 50)}...`
                          : parentQuestion.text}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Difficulty Level</InputLabel>
                <Select
                  value={formData.difficulty_level}
                  label="Difficulty Level"
                  onChange={(e) => setFormData({ ...formData, difficulty_level: e.target.value })}
                >
                  <MenuItem value="beginner">Beginner</MenuItem>
                  <MenuItem value="intermediate">Intermediate</MenuItem>
                  <MenuItem value="advanced">Advanced</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Max Score"
                type="number"
                value={formData.max_score}
                onChange={(e) => setFormData({ ...formData, max_score: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 1, max: 100 }}
                sx={{ minWidth: 120 }}
              />

              <TextField
                label="Duration (minutes)"
                type="number"
                value={formData.duration_minutes}
                onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 1, max: 60 }}
                sx={{ minWidth: 120 }}
              />
            </Box>

            {(effectiveQuestionType === 'listening_comprehension_set' ||
              effectiveQuestionType === 'reading_comprehension_set' ||
              !formData.parent_question_id) && (
              <TextField
                fullWidth
                label="Instructions (Optional)"
                multiline
                rows={2}
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              />
            )}

            {(effectiveQuestionType === 'listening_comprehension_set' ||
              effectiveQuestionType === 'reading_comprehension_set') && (
              <TextField
                label="Total Child Questions"
                type="number"
                value={formData.total_child_questions}
                onChange={(e) =>
                  setFormData({ ...formData, total_child_questions: parseInt(e.target.value) || 1 })
                }
                inputProps={{ min: 1, max: 10 }}
                sx={{ maxWidth: 200 }}
              />
            )}

            {(effectiveQuestionType === 'audio_comprehension' ||
              effectiveQuestionType === 'listening_comprehension_set') && (
              <TextField
                fullWidth
                label="Audio URL"
                value={formData.audio_url}
                onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
              />
            )}

            {(effectiveQuestionType === 'reading_comprehension' ||
              effectiveQuestionType === 'reading_comprehension_set' ||
              effectiveQuestionType === 'listening_comprehension_set') && (
              <TextField
                fullWidth
                label="Paragraph/Content"
                multiline
                rows={4}
                value={formData.paragraph}
                onChange={(e) => setFormData({ ...formData, paragraph: e.target.value })}
              />
            )}

            {formData.parent_question_id && (
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Question Number"
                  type="number"
                  value={formData.question_number}
                  onChange={(e) =>
                    setFormData({ ...formData, question_number: parseInt(e.target.value) || 1 })
                  }
                  inputProps={{ min: 1, max: 20 }}
                  sx={{ minWidth: 120 }}
                />
                <FormControl sx={{ minWidth: 150 }}>
                  <InputLabel>Depends on Parent</InputLabel>
                  <Select
                    value={formData.depends_on_parent ? 'true' : 'false'}
                    label="Depends on Parent"
                    onChange={(e) =>
                      setFormData({ ...formData, depends_on_parent: e.target.value === 'true' })
                    }
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}

            {effectiveQuestionType === 'multiple_choice' && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Answer Options
                </Typography>
                {formData.options.map((option, index) => (
                  <TextField
                    key={index}
                    fullWidth
                    label={`Option ${String.fromCharCode(65 + index)}`}
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...formData.options];
                      newOptions[index] = e.target.value;
                      setFormData({ ...formData, options: newOptions });
                    }}
                    sx={{ mb: 2 }}
                  />
                ))}
                <TextField
                  fullWidth
                  label="Correct Answer"
                  value={formData.correct_answer}
                  onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                  placeholder="Enter the correct answer (e.g., A, B, C, or D)"
                />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditDialogOpen(false);
              setEditingQuestion(null);
              setFormData(emptyFormData);
            }}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateQuestion}
            disabled={
              saving ||
              !formData.text ||
              !formData.section_id ||
              !formData.subsection_id ||
              !effectiveQuestionType ||
              !customTypeValid
            }
          >
            {saving ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ViewQuestions;
